import React, { useState } from 'react';
import {
    TERMS_SECTIONS,
    PRIVACY_SECTIONS,
    REFUND_SECTIONS,
    PRICING_SECTIONS,
    ENTITY_DISCLOSURE,
    LAST_UPDATED,
} from './legalContent';

const LEGAL_PAGES = {
    terms: {
        title: 'Terms of Service',
        tab: 'Terms',
        icon: '📋',
        color: '#3B82F6',
        colorDark: '#2563EB',
        bg: '#EFF6FF',
        intro: 'These Terms are an electronic agreement between you and FlatLoop. They cover who may use the service, what FlatLoop is and is not, how paid features work, and how disputes are handled. Please read them before using the platform.',
        sections: TERMS_SECTIONS,
    },
    privacy: {
        title: 'Privacy Notice',
        tab: 'Privacy',
        icon: '🔒',
        color: '#10B981',
        colorDark: '#059669',
        bg: '#D1FAE5',
        intro: 'This is our notice under the Digital Personal Data Protection Act, 2023. It sets out exactly what personal data we collect, why, who processes it, how long we keep it, how to withdraw your consent, and the rights you have as a Data Principal.',
        sections: PRIVACY_SECTIONS,
        showEntity: true,
    },
    refund: {
        title: 'Refund & Cancellation Policy',
        tab: 'Refunds',
        icon: '↩️',
        color: '#F59E0B',
        colorDark: '#D97706',
        bg: '#FEF3C7',
        intro: 'FlatLoop Prime is the only paid service on FlatLoop. This policy explains when a refund is available, how to ask for one, and how long it takes to reach you.',
        sections: REFUND_SECTIONS,
        showEntity: true,
    },
    pricing: {
        title: 'Pricing',
        tab: 'Pricing',
        icon: '💳',
        color: '#8B5CF6',
        colorDark: '#7C3AED',
        bg: '#EDE9FE',
        intro: 'Everything on FlatLoop is free except FlatLoop Prime, which unlocks contact numbers and unlimited chat. Here is exactly what it costs and what it includes.',
        sections: PRICING_SECTIONS,
        showEntity: true,
    },
};

const PAGE_ORDER = ['terms', 'privacy', 'refund', 'pricing'];

const LegalPage = ({ pageType = 'terms', onBack, onSwitchPage }) => {
    const [activeSection, setActiveSection] = useState(null);
    const page = LEGAL_PAGES[pageType] || LEGAL_PAGES.terms;

    return (
        <div style={{ background: '#F3F4F6', minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
            {/* Header Banner — using <header> so CSS rule skips adding extra padding-top */}
            <header style={{ background: `linear-gradient(135deg, ${page.color}, ${page.colorDark})`, padding: '100px 20px 80px', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative blobs */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '240px', height: '240px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '10%', width: '160px', height: '160px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <button
                        onClick={onBack}
                        style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '32px', padding: '10px 20px', borderRadius: '30px', fontSize: '14px', backdropFilter: 'blur(10px)', transition: 'background 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        Back to Home
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.2)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', backdropFilter: 'blur(10px)' }}>
                            {page.icon}
                        </div>
                        <div>
                            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Legal</p>
                            <h1 style={{ color: 'white', fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: '800', letterSpacing: '-1px', margin: 0 }}>{page.title}</h1>
                        </div>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '15px', marginTop: '8px' }}>Last updated: {LAST_UPDATED} · {page.sections.length} sections</p>
                </div>
            </header>

            {/* Tab Switch */}
            <div style={{ maxWidth: '800px', margin: '-24px auto 0', padding: '0 20px', position: 'relative', zIndex: 10 }}>
                {/* Four tabs now, so they scroll horizontally rather than squeezing
                    to unreadable widths on a phone. */}
                <div style={{ background: 'white', borderRadius: '20px', padding: '8px', display: 'flex', gap: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
                    {PAGE_ORDER.map((key) => {
                        const tabPage = LEGAL_PAGES[key];
                        const active = pageType === key;
                        return (
                            <button
                                key={key}
                                onClick={() => onSwitchPage(key)}
                                style={{
                                    flex: '1 0 auto', padding: '12px 16px', borderRadius: '14px', border: 'none',
                                    cursor: 'pointer', fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap',
                                    background: active ? tabPage.bg : 'transparent',
                                    color: active ? tabPage.color : '#6B7280',
                                    transition: 'all 0.2s',
                                }}
                            >
                                {tabPage.icon} {tabPage.tab}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '800px', margin: '32px auto 80px', padding: '0 20px' }}>
                {/* Intro */}
                <div style={{ background: 'white', borderRadius: '24px', padding: '32px', marginBottom: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', borderLeft: `4px solid ${page.color}` }}>
                    <p style={{ color: '#4B5563', fontSize: '16px', lineHeight: '1.8', margin: 0 }}>
                        {page.intro}
                    </p>
                </div>

                {/* Sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {page.sections.map((section) => (
                        <div
                            key={section.num}
                            onClick={() => setActiveSection(activeSection === section.num ? null : section.num)}
                            style={{ background: 'white', borderRadius: '20px', padding: '20px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', cursor: 'pointer', border: `2px solid ${activeSection === section.num ? page.color : 'transparent'}`, transition: 'all 0.2s' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ width: '36px', height: '36px', background: activeSection === section.num ? page.color : page.bg, color: activeSection === section.num ? 'white' : page.color, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', flexShrink: 0, transition: 'all 0.2s' }}>
                                        {section.num}
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>{section.title}</h3>
                                </div>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: activeSection === section.num ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </div>
                            {activeSection === section.num && (
                                <p style={{ margin: '16px 0 4px', paddingLeft: '52px', color: '#4B5563', fontSize: '15px', lineHeight: '1.8', animation: 'fadeIn 0.2s ease-out' }}>
                                    {section.text}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                {/* Seller / operator disclosure. Required to be published by the
                    Consumer Protection (E-Commerce) Rules, 2020 — shown on the pages
                    that concern a paid transaction. */}
                {page.showEntity && (
                    <div style={{ marginTop: '32px', background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
                        <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '800', color: '#111827' }}>Operator &amp; Grievance Details</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                            Published under the Consumer Protection (E-Commerce) Rules, 2020.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {ENTITY_DISCLOSURE.map(([label, value], i) => (
                                <div
                                    key={label}
                                    style={{
                                        display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px 0',
                                        borderBottom: i < ENTITY_DISCLOSURE.length - 1 ? '1px solid #F3F4F6' : 'none',
                                    }}
                                >
                                    <span style={{ flex: '0 0 180px', fontSize: '14px', color: '#6B7280', fontWeight: '600' }}>{label}</span>
                                    <span style={{ flex: 1, minWidth: '200px', fontSize: '14px', color: '#111827', fontWeight: '700', wordBreak: 'break-word' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                        <p style={{ margin: '20px 0 0', fontSize: '13px', color: '#6B7280', lineHeight: '1.7' }}>
                            Complaints are acknowledged within 48 hours and resolved within one month.
                            You may also contact the National Consumer Helpline on{' '}
                            <strong>1915</strong> or at{' '}
                            <a href="https://consumerhelpline.gov.in" target="_blank" rel="noreferrer" style={{ color: '#2563EB' }}>consumerhelpline.gov.in</a>.
                        </p>
                    </div>
                )}

                {/* Contact Card */}
                <div style={{ marginTop: '32px', background: 'linear-gradient(135deg, #1F2937, #374151)', borderRadius: '24px', padding: '32px', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h3 style={{ color: 'white', margin: '0 0 4px', fontSize: '20px', fontWeight: '800' }}>Have questions?</h3>
                        <p style={{ color: '#9CA3AF', margin: 0, fontSize: '15px' }}>Our team is happy to help you understand our policies.</p>
                    </div>
                    <a href="mailto:contact@flatloop.in" style={{ background: 'white', color: '#1F2937', padding: '12px 28px', borderRadius: '30px', fontWeight: '700', textDecoration: 'none', fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0, transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#E5E9E3'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                        contact@flatloop.in
                    </a>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default LegalPage;
